import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-history-quizzes", title: "Why History Quizzes?" },
  { id: "world-history", title: "World History Quizzes" },
  { id: "american-history", title: "American History Trivia" },
  { id: "war-history", title: "War & Military History" },
];

const quizResources = [
  { name: "History Buff Quiz", url: "https://www.britannica.com/quiz/history-buff-quiz", description: "Test your history knowledge" },
  { name: "WWII Fact or Fiction", url: "https://www.britannica.com/quiz/world-war-ii-fact-or-fiction", description: "World War II trivia" },
  { name: "All-American History", url: "https://www.britannica.com/quiz/all-american-history-quiz", description: "US history quiz" },
  { name: "Not-So-Common Knowledge", url: "https://www.britannica.com/quiz/not-so-common-knowledge", description: "Obscure historical facts" },
  { name: "Explore India Quiz", url: "https://www.britannica.com/quiz/explore-india", description: "India's history and culture" },
];

const relatedTopics = [
  { name: "Art Quizzes", path: "/quizzes/art-quizzes/", description: "Famous paintings and artists" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge tests" },
  { name: "Science Quizzes", path: "/quizzes/science-quizzes/", description: "Science and discovery" },
];

const HistoryQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="History Quizzes: Test Your Historical Knowledge"
      metaTitle="History Quizzes | World History & Historical Trivia 2025"
      metaDescription="Challenge your history knowledge with quizzes on world events, wars, famous figures, and civilizations. From ancient Rome to modern history - how much do you know?"
      keywords="history quizzes, world history trivia, american history quiz, war history quiz, historical events, history trivia test"
      canonicalUrl="/quizzes/history-quizzes/"
      category="History"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="History shapes our present and future. History quizzes let you explore ancient civilizations, pivotal wars, revolutionary leaders, and the events that changed the world—all while testing how well you remember what you learned in school."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-history-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">History Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Understanding history helps us understand the world we live in today. History quizzes are an engaging way to refresh your knowledge and discover events and figures you might have missed.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Remember key events:</strong> Important dates and their significance</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn about leaders:</strong> From emperors to revolutionaries</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Understand causes:</strong> Why events happened the way they did</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Prepare for exams:</strong> Great for AP History and college prep</span>
          </li>
        </ul>
      </section>

      <section id="world-history" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          World History <span className="text-gradient">Quizzes</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/history-buff-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">History Buff Quiz</a> covers events from ancient civilizations to modern times. Test your knowledge of:
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Ancient Civilizations</h4>
            <p className="text-sm text-muted-foreground">Egypt, Greece, Rome, Mesopotamia</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Medieval Period</h4>
            <p className="text-sm text-muted-foreground">Crusades, kingdoms, and feudalism</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Renaissance & Enlightenment</h4>
            <p className="text-sm text-muted-foreground">Art, science, and new ideas</p>
          </div>
          <a href="https://www.britannica.com/quiz/explore-india" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Explore India</h4>
            <p className="text-sm text-muted-foreground">India's government, geography, and history</p>
          </a>
        </div>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.britannica.com/quiz/not-so-common-knowledge" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Not-So-Common Knowledge</a> quizzes go beyond textbook facts to test obscure but fascinating historical details—like who hosted the first Olympic Games in 1900.
        </p>
      </section>

      <section id="american-history" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          American History <span className="text-gradient">Trivia</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/all-american-history-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">All-American History Quiz</a> tests your knowledge of United States history from the founding to today.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Founding Fathers:</strong> The men who shaped the nation</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Civil War:</strong> The conflict that divided America</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Constitutional knowledge:</strong> Amendments and their meanings</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Presidential trivia:</strong> Facts about US presidents</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes are perfect for students preparing for AP US History or anyone interested in understanding America's past.
        </p>
      </section>

      <section id="war-history" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          War & Military <span className="text-gradient">History</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://www.britannica.com/quiz/world-war-ii-fact-or-fiction" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">World War II: Fact or Fiction</a> is one of Britannica's most popular history quizzes, challenging common misconceptions about the deadliest conflict in history.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>World War I:</strong> The Great War and its aftermath</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>World War II:</strong> Battles, leaders, and turning points</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Cold War:</strong> The ideological conflict that shaped decades</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Ancient battles:</strong> From the Spartans to Alexander the Great</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Military history quizzes often reveal surprising facts—like the actual role of carrier pigeons in wartime communication or the myths versus realities of famous battles.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default HistoryQuizzes;
